import { CubeTexture as BABYLON_CubeTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/cubeTexture.js';
import { HDRCubeTexture as BABYLON_HDRCubeTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/hdrCubeTexture.js';
import { Texture as BABYLON_Texture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/texture.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Material as BABYLON_Material } from '@inter-ikea-betrakta-babylonjs/core/Materials/material.js';
import { MeshBuilder as BABYLON_MeshBuilder } from '@inter-ikea-betrakta-babylonjs/core/Meshes/meshBuilder.js';
import { RenderingGroupID } from '../Viewer.js';
import { ViewerMaterial } from '../material/ViewerMaterial.js';
import { deep_equals } from '../utility/utils.js';
import { vector_3 } from '../utility/Conversion.js';
import { get_file_extension } from '../../utils/FilePath.js';
const CUBEMAP_SIZE = 512; // the size on the cubetexture, if given hdr or longlat envmap.
const DDS = '.dds';
const ENV = '.env';
const HDR = '.hdr';
export class EnvironmentManager {
    constructor(_viewer, _ambient_light_controller) {
        this._viewer = _viewer;
        this._ambient_light_controller = _ambient_light_controller;
        this._skybox = null;
        this._environment_map_local_mode_settings = null; // null in this case means no local mode, i.e BabylonJS default behaviour.
        this._environment_map_url = null;
        this._environment_map_file_type = null;
        this._ready = Promise.resolve();
        this._scene = _viewer.scene;
        this._freezing_manager = _viewer.freezing_manager;
        this._asset_manager = _viewer.asset_manager;
        this._mark_scene_dirty = _viewer.mark_scene_dirty.bind(_viewer);
        if (this._viewer.has_default_view()) {
            // Skybox only works with the default view (better than nothing)
            // So before rendering the default view, we need to mark it as dirty for it to recalculate its world transform,
            // as it is dependant on the camera.
            this._viewer.get_default_view().before_render_observable.add((view) => {
                if (this._skybox === null)
                    return;
                this._skybox.position.fromIVector3(view.engine_camera.position);
                this._skybox._updateSubMeshesBoundingInfo(this._skybox.getWorldMatrix());
            });
        }
    }
    get ready() {
        return this._ready;
    }
    set_scene_environment(cubetexture) {
        const old_environment_map = this._scene.environmentTexture;
        this._scene.environmentTexture = cubetexture;
        // @Martin Disabling the light when there are no other lights in the scene causes webgl warnings.
        // TODO: Dispose instead. Cannot see this error. On what devices does this happen?
        this._ambient_light_controller.set_intensity(0);
        // Set cached local mode, relavant if user have tried to set it before envmap was loded
        this.set_environment_map_local_mode(this._environment_map_local_mode_settings);
        if (old_environment_map) {
            old_environment_map.dispose();
        }
        this._freezing_manager.set_all_materials_dirty();
        this._mark_scene_dirty();
    }
    set_environment_map(url, nullable_file_extension, filter_mips) {
        if (url === this.get_environment_map()) {
            // Nothing has changed, so we just early out
            return Promise.resolve();
        }
        if (url === null) {
            this.set_scene_environment(null);
            this._environment_map_url = null;
            this._environment_map_file_type = null;
            if (this._skybox) {
                this.set_skybox_material_texture(null, this._skybox.material);
            }
            this._mark_scene_dirty();
            this._ambient_light_controller.set_intensity(1);
            return Promise.resolve();
        }
        const file_extension = get_file_extension(url, nullable_file_extension !== null && nullable_file_extension !== void 0 ? nullable_file_extension : undefined);
        if (file_extension !== DDS && file_extension !== ENV && file_extension !== HDR) {
            throw new Error('Viewer: Unsupported environment map format. Please use: .env, .dds or .hdr');
        }
        const load_promise = new Promise((resolve, reject) => {
            const on_loaded = () => {
                // Set cached local mode, relavant if user have tried to set it before envmap was loded
                this.apply_environment_map_local_mode();
                if (this._skybox) {
                    this.set_skybox_material_texture(url, this._skybox.material);
                    this._mark_scene_dirty();
                }
                resolve();
            };
            const on_error = (message, exception) => {
                reject(new Error(message));
            };
            this._environment_map_url = url;
            if (file_extension.length > 1) {
                this._environment_map_file_type = file_extension.slice(1); // Need to remember file extension for when we save a serialized scene, since the url is converted to a dataurl
            }
            if (this._viewer.get_config().headless) {
                // Due to limitations in headless, we can't load cube textures. So we have to mock it.
                setTimeout(() => {
                    // setTimeout to get delay effect seen when loading textures, which is async.
                    on_loaded();
                }, 0);
            }
            else if (file_extension === DDS || file_extension === ENV) {
                const cubetexture = new BABYLON_CubeTexture(url, this._scene, undefined, undefined, undefined, on_loaded, on_error, undefined, true, file_extension);
                this.set_scene_environment(cubetexture);
            }
            else if (file_extension === HDR) {
                const cubetexture = new BABYLON_HDRCubeTexture(url, this._scene, CUBEMAP_SIZE, undefined, undefined, undefined, filter_mips, on_loaded, on_error);
                this.set_scene_environment(cubetexture);
            }
        });
        this._ready = load_promise;
        return load_promise;
    }
    get_environment_map() {
        return this._environment_map_url;
    }
    set_environment_map_intensity(intensity) {
        if (intensity === this._scene.environmentIntensity)
            return;
        this._scene.environmentIntensity = intensity;
        this._freezing_manager.set_all_materials_dirty();
        this._mark_scene_dirty();
    }
    get_environment_map_intensity() {
        return this._scene.environmentIntensity;
    }
    get_environment_map_rotation() {
        let rotation = 0;
        if (this._scene.environmentTexture instanceof BABYLON_CubeTexture || this._scene.environmentTexture instanceof BABYLON_HDRCubeTexture) {
            rotation = this._scene.environmentTexture.rotationY;
        }
        return rotation;
    }
    set_environment_map_rotation(rotation) {
        if (this._scene.environmentTexture instanceof BABYLON_CubeTexture || this._scene.environmentTexture instanceof BABYLON_HDRCubeTexture) {
            if (rotation === this._scene.environmentTexture.rotationY)
                return;
            this._scene.environmentTexture.rotationY = rotation;
            if (this._skybox) {
                const skybox_pbr_material = this._skybox.material;
                const skybox_cube_texture = skybox_pbr_material.reflectionTexture;
                skybox_cube_texture.rotationY = rotation;
            }
            this._freezing_manager.set_all_materials_dirty();
            this._mark_scene_dirty();
        }
    }
    set_environment_map_local_mode(settings) {
        if (this._environment_map_local_mode_settings === null && settings === null)
            return;
        if (this._environment_map_local_mode_settings !== null &&
            settings !== null &&
            deep_equals(this._environment_map_local_mode_settings, settings)) {
            return;
        }
        if (settings === null) {
            this._environment_map_local_mode_settings = null;
        }
        else {
            // Make sure we don't copy any references
            this._environment_map_local_mode_settings = {
                position: Object.assign({}, settings.position),
                size: Object.assign({}, settings.size)
            };
        }
        this.apply_environment_map_local_mode();
    }
    apply_environment_map_local_mode() {
        // Apply settings if environment is loaded
        if (this._scene.environmentTexture instanceof BABYLON_CubeTexture || this._scene.environmentTexture instanceof BABYLON_HDRCubeTexture) {
            // if settings is null, no local mode, default babylon behaviour.
            if (this._environment_map_local_mode_settings === null) {
                // @ts-ignore
                this._scene.environmentTexture.boundingBoxSize = null; // Need to ignore TS compiler due to wrong types in BabylonJS
                // @ts-ignore
                this._scene.environmentTexture.boundingBoxPosition = null; // Need to ignore TS compiler due to wrong types in BabylonJS
            }
            else {
                this._scene.environmentTexture.boundingBoxSize = vector_3(this._environment_map_local_mode_settings.size);
                this._scene.environmentTexture.boundingBoxPosition = vector_3(this._environment_map_local_mode_settings.position);
            }
            // Update all materials and trigger new render
            this._freezing_manager.set_all_materials_dirty();
            this._mark_scene_dirty();
        }
    }
    get_environment_map_local_mode() {
        if (this._scene.environmentTexture instanceof BABYLON_CubeTexture || this._scene.environmentTexture instanceof BABYLON_HDRCubeTexture) {
            const size = this._scene.environmentTexture.boundingBoxSize; // BabylonJS 4.1 have wrong type for boundBox in a envmap, realtyp BABYLON_Nullable<Vector3> | undefined
            const position = this._scene.environmentTexture.boundingBoxPosition; // BabylonJS 4.1 have wrong type for boundBox in a envmap, realtyp BABYLON_Nullable<Vector3> | undefined
            if (size instanceof BABYLON_Vector3 && position instanceof BABYLON_Vector3) {
                return {
                    size: { x: size.x, y: size.y, z: size.z },
                    position: { x: position.x, y: position.y, z: position.z }
                };
            }
        }
        return null;
    }
    show_environment_map_as_a_skybox(mesh_manager) {
        if (!this._skybox) {
            const sky_box_material = new ViewerMaterial(`material_${this._asset_manager.materials.length()}`, this._scene, this._viewer);
            sky_box_material.sideOrientation = BABYLON_Material.CounterClockWiseSideOrientation;
            sky_box_material.backFaceCulling = false;
            sky_box_material.disableDepthWrite = true; // Do not write to depth, let all other rendered object overwrite the pixels rendered from this box, since this box represents somethings that's really far away
            if (this._environment_map_url) {
                this.set_skybox_material_texture(this._environment_map_url, sky_box_material);
            }
            // Create mesh
            const size = 2.0;
            const options = {
                width: size,
                height: size,
                depth: size
            };
            const skybox_object = BABYLON_MeshBuilder.CreateBox('skybox', options, this._scene);
            this._skybox = skybox_object;
            this._skybox.material = sky_box_material;
            this._skybox.renderingGroupId = RenderingGroupID.SKYBOX_RENDERING_GROUP;
        }
        else {
            if (this._environment_map_url) {
                this.set_skybox_material_texture(this._environment_map_url, this._skybox.material);
            }
        }
    }
    set_skybox_material_texture(environment_map_url, sky_box_material) {
        if (environment_map_url) {
            sky_box_material.reflectionTexture = new BABYLON_CubeTexture(environment_map_url, this._scene);
            sky_box_material.reflectionTexture.coordinatesMode = BABYLON_Texture.SKYBOX_MODE;
        }
        else {
            sky_box_material.reflectionTexture = null;
        }
    }
    get_environment_map_file_type() {
        return this._environment_map_file_type;
    }
    get_skybox() {
        return this._skybox;
    }
    dispose() {
        var _a, _b;
        if (this._skybox) {
            (_a = this._skybox.material) === null || _a === void 0 ? void 0 : _a.dispose();
            this._skybox.dispose();
            this._skybox = null;
        }
        (_b = this._scene.environmentTexture) === null || _b === void 0 ? void 0 : _b.dispose();
    }
}
//# sourceMappingURL=EnvironmentManager.js.map